import { useEffect, useRef, useState } from 'react'
import { NOTICE_CLASS, StagingBanner, Button } from '@region13/ui'
import { PHONE_TAP } from './ui'
import { api, ApiError } from '../lib/api'
import { Shell } from './Shell'
import { Card } from './ui/Card'

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? ''

const inputClass =
  'w-full min-h-[44px] px-3 rounded-lg border border-line bg-surface text-base text-ink ' +
  // Explicit placeholder color at ink-soft (6.8:1) — the UA default can dip
  // below the 4.5:1 contrast floor.
  'placeholder:text-ink-soft ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-maroon/30 focus:border-brand-maroon'
const labelClass = 'text-xs font-semibold uppercase tracking-wide text-brand-maroon'

/** The email-code sign-in flow (request a 6-digit code, verify it), gated by
 *  an optional Turnstile widget. Moved out of the old App.tsx unchanged in
 *  behavior: same steps, same 429/403/expired-code copy, same dev_code
 *  shortcut in local dev. `routes/__root.tsx` renders this whenever `me.email`
 *  is null and swaps to the authenticated shell once `onSignedIn` fires. */
export function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [devCode, setDevCode] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const widgetRef = useRef<HTMLDivElement | null>(null)
  const widgetId = useRef<string | null>(null)

  // Render the Turnstile widget on the email step once the script + container
  // are ready (api.js loads async). Tear it down on cleanup and null the id so
  // returning to the email step (e.g. "Use a different email") renders fresh
  // rather than leaving an empty widget that permanently disables send.
  useEffect(() => {
    if (step !== 'email' || !SITE_KEY) return
    let cancelled = false
    let timer: ReturnType<typeof setInterval> | null = null
    const tryRender = (): boolean => {
      if (cancelled || widgetId.current) return true
      const el = widgetRef.current
      if (!el || !window.turnstile) return false
      widgetId.current = window.turnstile.render(el, {
        sitekey: SITE_KEY,
        callback: (t) => setToken(t),
        'error-callback': () => setToken(null),
        'expired-callback': () => setToken(null),
      })
      return true
    }
    if (!tryRender()) {
      timer = setInterval(() => {
        if (tryRender() && timer) clearInterval(timer)
      }, 200)
    }
    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current)
        widgetId.current = null
      }
      setToken(null)
    }
  }, [step])

  function resetWidget() {
    setToken(null)
    if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current)
  }

  async function requestCode() {
    setBusy(true)
    setError(null)
    try {
      const result = await api.requestCode(email, token)
      setDevCode(result.dev_code ?? null)
      setStep('code')
    } catch (err) {
      resetWidget()
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many attempts. Please wait a few minutes and try again.')
      } else if (err instanceof ApiError && err.status === 403) {
        setError('Bot check failed. Please try again.')
      } else {
        setError('Could not send a code. Check the email and try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  async function verifyCode() {
    setBusy(true)
    setError(null)
    try {
      await api.verifyCode(email, code)
      onSignedIn()
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many attempts. Please wait a few minutes and try again.')
      } else {
        setError('That code did not match. Request a new one if it expired.')
      }
    } finally {
      setBusy(false)
    }
  }

  // When a site key is configured, require a solved widget before enabling send.
  const sendDisabled = busy || !email || (SITE_KEY !== '' && !token)

  return (
    <Shell>
      <Card>
        {/* Above the step switch, so it survives into the code step. */}
        <StagingBanner />
        {step === 'email' ? (
          <>
            <h1 className="text-xl font-extrabold text-brand-red-dark m-0">Coach sign-in</h1>
            <p className="text-sm text-ink-soft m-0">
              Enter your email and we will send you a single-use code.
            </p>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className={labelClass}>
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={inputClass}
              />
            </div>
            {SITE_KEY && <div ref={widgetRef} className="turnstile" />}
            <Button className={PHONE_TAP} variant="primary" disabled={sendDisabled} onClick={requestCode}>
              Send me a code
            </Button>
          </>
        ) : (
          <>
            <h1 className="text-xl font-extrabold text-brand-red-dark m-0">Enter your code</h1>
            <p className="text-sm text-ink-soft m-0">
              We sent a 6-digit code to <strong className="text-ink">{email}</strong>.
            </p>
            {devCode && (
              <p className={NOTICE_CLASS}>
                Dev code: <code className="font-bold font-mono">{devCode}</code>
              </p>
            )}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="code" className={labelClass}>
                Code
              </label>
              <input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                className={inputClass}
              />
            </div>
            <Button className={PHONE_TAP} variant="primary" disabled={busy || code.length !== 6} onClick={verifyCode}>
              Verify
            </Button>
            <button
              type="button"
              className={`${PHONE_TAP} text-sm font-medium text-brand-red-dark hover:underline`}
              onClick={() => {
                setStep('email')
                resetWidget()
              }}
            >
              Use a different email
            </button>
          </>
        )}
        {error && (
          <p role="alert" className="text-sm font-bold text-brand-red-dark m-0">
            {error}
          </p>
        )}
      </Card>
    </Shell>
  )
}
